import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY, merge, Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, filter, finalize, map, shareReplay, switchMap } from 'rxjs/operators';
import { normalizeNodeId } from '@gt/para/shared/para-node-id.util';
import { getOrCreateCachedObservable, pruneCacheExceptPrefix } from '../utils/para-http-cache.util';
import { ReportService } from '@gt/_services/report-service';
import { ParaApiRootService } from './para-api-root.service';
import { ParaOrgStateService } from './para-org-state.service';
import { ParaLoadingService } from './para-loading.service';
import { ParaProgressReportDto } from '../models/para-progress-report.dto';
import { ParaSelectedAssessmentService } from './para-selected-assessment.service';

/**
 * PARA progress table: HTTP + cache for {@code /para/reports/getProgressReport} and derived {@link progressReport$}.
 * Coordinates with {@link ReportService} only where needed (classic replay after score submit); does not modify ReportService.
 */
@Injectable({ providedIn: 'root' })
export class ParaProgressReportService {
	private readonly paraProgressReportHttpByCacheKey = new Map<string, Observable<ParaProgressReportDto | null>>();
	private readonly paraProgressTableRefreshSubject = new Subject<number>();

	/** Emits normalized classroom node id when the PARA table should refetch (e.g. after score submit). */
	readonly paraProgressTableRefresh$ = this.paraProgressTableRefreshSubject.asObservable();

	constructor(
		private readonly paraOrgState: ParaOrgStateService,
		private readonly httpClient: HttpClient,
		private readonly loaderService: ParaLoadingService,
		private readonly reportService: ReportService,
		private readonly paraApi: ParaApiRootService,
		private readonly paraSelectedAssessment: ParaSelectedAssessmentService
	) {
		this.paraOrgState.selectedClassroom$
			.pipe(
				filter((data: any) => !!data && data.id != null && data.id !== ''),
				map((data: any) => normalizeNodeId(data.id)),
				filter((id): id is number => id != null),
				distinctUntilChanged()
			)
			.subscribe((nid) => this.pruneHttpCacheExcept(nid));
	}

	/**
	 * GET {@code /para/reports/getProgressReport/nodeId/:id} — shareReplay-cached per node, independent of classic {@link ReportService.getProgressReport}.
	 */
	getParaProgressReport(nodeId: any, assessmentId?: string | null): Observable<ParaProgressReportDto | null> {
		const id = normalizeNodeId(nodeId);
		if (id == null) {
			return of(null);
		}
		const aid = (assessmentId || '').trim();
		const cacheKey = aid ? `${id}::${aid}` : String(id);
		return getOrCreateCachedObservable(
			this.paraProgressReportHttpByCacheKey,
			cacheKey,
			() => {
				this.loaderService.setLoadingProgressTable(true);
				let params = new HttpParams();
				if (aid) {
					params = params.set('assessmentId', aid);
				}
				return this.httpClient.get<ParaProgressReportDto>(this.paraApi.reportsUrl(`getProgressReport/nodeId/${id}`), { params }).pipe(
					finalize(() => this.loaderService.setLoadingProgressTable(false))
				);
			},
			{ refCount: false }
		);
	}

	/** Drop cached PARA progress report for this node (e.g. before refetch). */
	invalidateParaProgressCache(nodeId: number): void {
		const prefix = `${nodeId}::`;
		for (const k of [...this.paraProgressReportHttpByCacheKey.keys()]) {
			if (k === String(nodeId) || k.indexOf(prefix) === 0) {
				this.paraProgressReportHttpByCacheKey.delete(k);
			}
		}
	}

	/** Notify PARA progress table listeners to refetch for this classroom node. */
	emitParaProgressRefresh(nodeId: number): void {
		this.paraProgressTableRefreshSubject.next(nodeId);
	}

	/** When PARA classroom scope changes, drop cached progress reports for other nodes. */
	pruneHttpCacheExcept(nid: number): void {
		const keep = String(nid);
		pruneCacheExceptPrefix(this.paraProgressReportHttpByCacheKey, keep, `${nid}::`);
	}

	readonly progressReport$: Observable<ParaProgressReportDto> = merge(
		this.paraOrgState.selectedClassroom$.pipe(
			distinctUntilChanged((a, b) => normalizeNodeId(a?.id as string | number) === normalizeNodeId(b?.id as string | number)),
			switchMap((classroom) => {
				const nid = normalizeNodeId(classroom?.id as string | number);
				if (nid == null) {
					return EMPTY;
				}
				return this.paraSelectedAssessment.resolvedAssessmentId$.pipe(switchMap((aid) => this.getParaProgressReport(nid, aid)));
			})
		),
		// Refetch using the node id from {@link refreshProgressReport} — do not require it to match
		// {@link ParaOrgStateService.selectedClassroom$} (that equality can fail and block updates after exempt toggle, etc.).
		this.paraProgressTableRefresh$.pipe(
			switchMap((rid) =>
				this.paraSelectedAssessment.resolvedAssessmentId$.pipe(switchMap((aid) => this.getParaProgressReport(rid, aid)))
			)
		)
	).pipe(
		filter((data): data is ParaProgressReportDto => data != null),
		shareReplay({ bufferSize: 1, refCount: false })
	);

	/**
	 * After scores: invalidate PARA cache, refresh classic {@link ReportService.progressReport}, then refetch PARA endpoint for subscribers.
	 */
	refreshProgressReport(nodeId: any): void {
		const id = normalizeNodeId(nodeId);
		if (id == null) {
			return;
		}
		this.invalidateParaProgressCache(id);
		this.reportService.refreshProgressReport(nodeId);
		this.emitParaProgressRefresh(id);
	}
}
